import React, { useMemo, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  IconButton, 
  Typography,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField
} from '@mui/material';
import { 
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth,
  isSameMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameDay,
  parseISO
} from 'date-fns';
import { pl } from 'date-fns/locale';

const CalendarHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const MonthTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Cinzel, serif',
  fontSize: '1.5rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  textTransform: 'capitalize',
}));

const CalendarGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: theme.spacing(1),
  width: '100%',
  height: 'calc(100% - 80px)', // Odejmujemy wysokość nagłówka
  gridTemplateRows: 'auto repeat(6, 1fr)', // Header + max 6 tygodni
}));

const DayHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  textAlign: 'center',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const DayCell = styled(Paper)(({ theme }) => ({
  position: 'relative',
  height: '100%',
  minHeight: '100px',
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  '&[data-is-current-month="false"]': {
    background: theme.palette.grey[800],
    color: theme.palette.grey[600],
    opacity: 0.7,
  },
  '&[data-is-current-month="true"]': {
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  '&[data-is-today="true"]': {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '&[data-is-today="false"]': {
    border: '1px solid rgba(255,255,255,0.1)',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover, 
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '80px',
  }
}));

const DayNumber = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  position: 'absolute',
  top: '8px',
  left: '8px',
  '&[data-is-current-month="true"]': {
    color: theme.palette.text.primary,
  },
  '&[data-is-current-month="false"]': {
    color: theme.palette.text.disabled,
  },
}));

const ShiftsList = styled(Box)(({ theme }) => ({
  marginTop: '28px',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  overflow: 'auto',
  maxHeight: 'calc(100% - 28px)',
}));

// eslint-disable-next-line no-unused-vars
const formatUserName = (user) => {
  if (!user) return '';
  return user.first_name && user.last_name 
    ? `${user.first_name} ${user.last_name}`
    : user.username;
};

// eslint-disable-next-line no-unused-vars
const formatUserInitials = (user) => {
  if (!user) return '';
  if (user.first_name && user.last_name) {
    // Pobierz pierwszą literę imienia
    const firstInitial = user.first_name[0];
    // Pobierz pierwszą literę nazwiska (po podzieleniu na słowa dla dwuczłonowych nazwisk)
    const lastNameWords = user.last_name.split(' ');
    const lastInitial = lastNameWords[0][0];
    return `${firstInitial}${lastInitial}`.toUpperCase();
  }
  return user.username.substring(0, 2).toUpperCase();
};

const formatLocationShortName = (location) => {
  const shortNames = {
    'Panorama M&M GOLD': 'PAN',
    'Panorama VVS': 'VVS',
    'Modo M&M GOLD': 'MODO',
    'Klif M&M GOLD': 'KLIF',
    'Galeria Północna M&M GOLD': 'GP',
    'Stary Browar M&M GOLD': 'SB',
    'Marbella': 'MB'
  };
  return shortNames[location] || location;
};
// eslint-disable-next-line no-unused-vars
const getStatusLabel = (status) => {
  switch (status) {
    case 'approved':
      return 'Zatwierdzony';
    case 'rejected':
      return 'Odrzucony';
    default:
      return 'Oczekujący';
  }
};

const ShiftChip = ({ shift, isAdmin, currentUser, onClick, onApprove, onReject }) => {
  // State for the rejection dialog
  const [rejectDialogOpen, setRejectDialogOpen] = React.useState(false);
  const [rejectionReason, setRejectionReason] = React.useState('');
  
  // Sprawdzamy, czy bieżący użytkownik jest właścicielem zmiany
  const isOwner = useMemo(() => {
    return currentUser && shift.user_id === currentUser.uid;
  }, [currentUser, shift]);

  // Określamy kolory w zależności od statusu zmiany
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return {
          bg: '#a5d6a7',                 // Material UI Green 200 - bardziej nasycony i widoczny zielony
          border: '#4caf50',             // Material UI Green 500
          text: '#1b5e20'                // Material UI Green 900
        };
      case 'rejected':
        return {
          bg: '#ef9a9a',                 // Material UI Red 200 - bardziej widoczny czerwony
          border: '#f44336',             // Material UI Red 500
          text: '#b71c1c'                // Material UI Red 900
        };
      case 'pending':
        return {
          bg: '#e0e0e0',                 // Material UI Grey 300 - jaśniejszy szary
          border: '#9e9e9e',             // Material UI Grey 500
          text: '#424242'                // Material UI Grey 800
        };
      default:
        return {
          bg: '#bbdefb',                 // Material UI Blue 100
          border: '#2196f3',             // Material UI Blue 500
          text: '#0d47a1'                // Material UI Blue 900
        };
    }
  };
  
  // eslint-disable-next-line no-unused-vars
  const colors = getStatusColor(shift.status);
  
  // Handle approval button click
  const handleApprove = (e) => {
    e.stopPropagation();
    console.log('Zatwierdzam zmianę:', shift);
    if (onApprove) {
      onApprove(shift);
    }
  };
  
  // Handle rejection dialog
  const handleRejectClick = (e) => {
    e.stopPropagation();
    setRejectDialogOpen(true);
  };
  
  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setRejectionReason('');
  };
  
  const handleRejectConfirm = () => {
    if (onReject) {
      onReject(shift, rejectionReason);
    }
    setRejectDialogOpen(false);
    setRejectionReason('');
  };
  
  // Get status label for display
  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved': return 'Zatwierdzona';
      case 'rejected': return 'Odrzucona';
      case 'pending': return 'Oczekująca';
      default: return '';
    }
  };

  return (
    <React.Fragment>
      <Tooltip
        title={
          <React.Fragment>
            <Typography variant="subtitle2">
              {shift.user_name || 'Brak nazwy'}
            </Typography>
            <Typography variant="body2">
              {shift.location} - {shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)}
            </Typography>
            <Typography variant="body2">
              Status: {getStatusLabel(shift.status)}
            </Typography>
            {isAdmin && shift.status === 'pending' && (
              <Typography variant="caption">
                Kliknij, aby zatwierdzić/odrzucić
              </Typography>
            )}
            {shift.status === 'rejected' && shift.rejection_reason && (
              <Typography variant="caption" color="error">
                Powód: {shift.rejection_reason}
              </Typography>
            )}
          </React.Fragment>
        }
        arrow
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Chip
            label={isAdmin ? (shift.user_name || 'Brak nazwy') : (formatLocationShortName(shift.location) || 'Brak lok.')}
            size="small"
            onClick={onClick}
            sx={{
              margin: '2px',
              backgroundColor: shift.status === 'approved' ? '#4caf50 !important' : 
                                shift.status === 'rejected' ? '#f44336 !important' : 
                                '#9e9e9e !important',
              color: '#ffffff !important',
              border: 'none',
              fontWeight: 'bold',
              '& .MuiChip-label': {
                paddingLeft: 1,
                paddingRight: 1,
                fontSize: '0.675rem',
              },
              cursor: 'pointer',
              // Tylko admin lub właściciel zmiany mogą wchodzić w interakcje
              opacity: isAdmin || isOwner ? 1 : 0.7,
              pointerEvents: isAdmin || isOwner ? 'auto' : 'none',
            }}
          />
          {isAdmin && shift.status === 'pending' && (
            <Box sx={{ display: 'flex', ml: 0.5 }}>
              <IconButton 
                onClick={handleApprove} 
                size="small" 
                sx={{ padding: 0.5, bgcolor: 'rgba(76, 175, 80, 0.1)', '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.2)' } }}
              >
                <CheckIcon fontSize="small" color="success" />
              </IconButton>
              <IconButton onClick={handleRejectClick} size="small" sx={{ padding: 0.5 }}>
                <CloseIcon fontSize="small" color="error" />
              </IconButton>
            </Box>
          )}
        </Box>
      </Tooltip>
      
      {/* Dialog odrzucania zmiany */}
      <Dialog open={rejectDialogOpen} onClose={handleRejectCancel}>
        <DialogTitle>Odrzuć zmianę</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="rejection-reason"
            label="Powód odrzucenia"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectCancel}>Anuluj</Button>
          <Button onClick={handleRejectConfirm} color="error">
            Odrzuć
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

const weekDays = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Niedz'];

const MonthlyCalendar = React.memo(({ shifts = [], onDayClick, onShiftClick, onApproveShift, onRejectShift, isAdmin, currentDate, onMonthChange }) => {
  const monthStart = useMemo(() => startOfMonth(currentDate), [currentDate]);
  const monthEnd = useMemo(() => endOfMonth(currentDate), [currentDate]);
  const calendarStart = useMemo(() => startOfWeek(monthStart, { weekStartsOn: 1 }), [monthStart]);
  const calendarEnd = useMemo(() => endOfWeek(monthEnd, { weekStartsOn: 1 }), [monthEnd]);

  // Generuj tablicę dni
  const days = useMemo(() => {
    const daysArray = [];
    let day = calendarStart;

    while (day <= calendarEnd) {
      daysArray.push(day);
      day = addDays(day, 1);
    }
    return daysArray;
  }, [calendarStart, calendarEnd]);

  const handlePreviousMonth = useCallback(() => {
    onMonthChange(subMonths(currentDate, 1));
  }, [currentDate, onMonthChange]);

  const handleNextMonth = useCallback(() => {
    onMonthChange(addMonths(currentDate, 1));
  }, [currentDate, onMonthChange]);

  // Grupuj zmiany po datach
  const shiftsByDate = useMemo(() => shifts.reduce((acc, shift) => {
    const shiftDate = parseISO(shift.date);
    const date = format(shiftDate, 'yyyy-MM-dd');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(shift);
    return acc;
  }, {}), [shifts]);

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <CalendarHeader>
        <IconButton onClick={handlePreviousMonth} size="large">
          <ChevronLeftIcon />
        </IconButton>
        <MonthTitle>
          {format(currentDate, 'LLLL yyyy', { locale: pl })}
        </MonthTitle>
        <IconButton onClick={handleNextMonth} size="large">
          <ChevronRightIcon />
        </IconButton>
      </CalendarHeader>

      <Box sx={{ flex: 1, overflow: 'auto', padding: 1 }}>
        <CalendarGrid>
          {/* Week days header */}
          {weekDays.map((dayName) => (
            <DayHeader key={dayName}>
              <Typography variant="subtitle2">
                {dayName}
              </Typography>
            </DayHeader>
          ))}

          {/* Calendar days */}
          {days.map((day) => {
            const dateKey = format(day, 'yyyy-MM-dd');
            const dayShifts = shiftsByDate[dateKey] || [];

            return (
              <DayCell 
                key={day.toISOString()}
                elevation={0}
                data-is-current-month={isSameMonth(day, currentDate)}
                data-is-today={isSameDay(day, new Date())}
                onClick={() => onDayClick(day)}
              >
                <DayNumber 
                  data-is-current-month={isSameMonth(day, currentDate)}
                >
                  {format(day, 'd')}
                </DayNumber>

                <ShiftsList>
                  {dayShifts.map(shift => (
                    <ShiftChip
                      key={shift.id}
                      shift={shift}
                      currentUser={null}
                      onClick={(e) => {
                        e.stopPropagation();
                        onShiftClick(shift);
                      }}
                      onApprove={(shift) => onApproveShift && onApproveShift(shift)}
                      onReject={(shift, reason) => onRejectShift && onRejectShift(shift, reason)}
                      isAdmin={isAdmin}
                    />
                  ))}
                </ShiftsList>
              </DayCell>
            );
          })}
        </CalendarGrid>
      </Box>
    </Box>
  );
});

MonthlyCalendar.displayName = 'MonthlyCalendar';

export default MonthlyCalendar;
